import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, Search, User as UserIcon, LogOut } from 'lucide-react';
import { reportApi } from '../services/api.ts';

interface User {
  id: number;
  username: string;
  name: string;
  nickname?: string;
  displayName?: string;
  email: string;
}

interface HeaderProps {
  onToggleSidebar: () => void;
  user?: User;
  onLogout?: () => void;
}

export default function Header({ onToggleSidebar, user, onLogout }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setShowSearchResults(value.length > 0);
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shadow-sm">
      <div className="flex items-center space-x-4">
        <button
          onClick={onToggleSidebar}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <Menu className="w-5 h-5 text-slate-600" />
        </button>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 pr-4 py-2 w-80 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          {showSearchResults && searchTerm && (
            <div className="absolute top-full left-0 mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              <div className="p-3 border-b border-slate-100 bg-slate-50">
                <p className="text-xs text-slate-500">Search for "{searchTerm}" in:</p>
                <div className="flex gap-2 mt-2">
                  <button 
                    onClick={() => navigate(`/company?search=${encodeURIComponent(searchTerm)}`)}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                  >
                    Companies
                  </button>
                  <button 
                    onClick={() => navigate(`/license?search=${encodeURIComponent(searchTerm)}`)}
                    className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200"
                  >
                    Licenses
                  </button>
                  <button 
                    onClick={() => navigate(`/expense?search=${encodeURIComponent(searchTerm)}`)}
                    className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded-full hover:bg-orange-200"
                  >
                    Expenses
                  </button>
                </div>
              </div>
              <div className="p-2 text-xs text-slate-400 text-center">
                Press Enter or click a category to search
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-3 pl-4 border-l border-slate-200">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
            <UserIcon className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-slate-800">{user?.displayName || user?.nickname || user?.name || user?.username || 'Admin'}</p>
          </div>
          {onLogout && (
            <button
              onClick={onLogout}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600 hover:text-red-600"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
